// =============================================================
//  Bilingual copy (Dutch default, English optional).
//  IMPORTANT: no em dashes anywhere in user-facing text.
//  The Dutch object defines the shape; the English object must
//  match it (enforced by the `Dict` type below).
// =============================================================

import astralImg from '../imgs/astral-models.png';

const nl = {
  skipToContent: 'Ga naar de inhoud',

  nav: {
    home: 'Home',
    services: 'Diensten',
    work: 'Werk',
    about: 'Over mij',
    contact: 'Contact',
    cta: 'Neem contact op',
    language: 'Taal',
    toDark: 'Donkere modus',
    toLight: 'Lichte modus',
    openMenu: 'Menu openen',
    closeMenu: 'Menu sluiten',
  },

  footer: {
    tagline:
      'Websites voor kleine ondernemingen. Prachtig gebouwd, fris gehouden, vanaf €150 per maand zonder opstartkosten.',
    exploreTitle: 'Ontdek',
    contactTitle: 'Neem contact op',
    location: 'Antwerpen / Kapellen, België',
    links: { home: 'Home', services: 'Diensten & prijzen', work: 'Werk', about: 'Over mij', contact: 'Contact' },
  },

  home: {
    eyebrow: 'Websites voor kleine ondernemingen',
    titlePart1: 'Een website waar je bedrijf ',
    titleAccent: 'trots op is.',
    titlePart2: '',
    leadPre: 'Mooie, snelle websites voor restaurants, kappers, bloemisten en vakmensen. Vanaf ',
    leadStrong: '€150 per maand, zonder opstartkosten',
    leadPost: ', en na de lancering netjes onderhouden en up-to-date gehouden.',
    ctaPrimary: 'Vertel me over je zaak',
    ctaSecondary: 'Bekijk mijn werk',
    chips: ['Geen opstartkosten', 'Updates inbegrepen', 'Je hebt rechtstreeks contact met mij'],
    value: {
      from: 'Vanaf',
      price: '€150',
      unit: '/mnd',
      priceNote: 'Geen opstartkosten. Nooit.',
      typicalStudio: 'Gemiddeld bureau',
      oldPrice: '€300+',
      oldNote: 'vooraf, nog voor er iets bestaat',
      title: 'Geen hoge rekening vooraf.',
      body: 'De meeste bureaus vragen €300 of meer voordat ze beginnen, en laten je daarna aan je lot over. Ik niet. Je spreidt de kosten in een eenvoudig maandbedrag, en ik houd je website onderhouden en up-to-date zolang we samenwerken.',
      link: 'Zo werkt het maandmodel',
    },
    processEyebrow: 'Hoe het werkt',
    processTitle: 'Vier eenvoudige stappen, zonder vakjargon',
    processLead: 'Van het eerste gesprek tot een site die steeds beter wordt. Zo pakken we het aan.',
    teaser: {
      eyebrow: 'Uitgelicht werk',
      title: 'Astral Models',
      body: 'Een hedendaags modellenbureau dat een site nodig had die net zo verzorgd is als hun talent. We bepaalden samen precies wat ze nodig hadden, ik deelde schetsen waarop ze reageerden, en ik bouwde een strakke, snelle site. Nog steeds regelmatig bijgewerkt terwijl ze groeien.',
      cta: 'Bekijk de case',
      live: 'Bezoek de live site',
    },
    trustEyebrow: 'Waarom ik',
    trustTitle: 'Duidelijk, en aan jouw kant',
    cta: {
      title: 'Klaar voor een website die net zo hard werkt als jij?',
      body: 'Vertel me iets over je zaak en ik kom bij je terug met ideeën. Geen druk, geen vakjargon.',
      primary: 'Vertel me over je zaak',
      link: 'Bekijk diensten en prijzen',
    },
  },

  services: {
    headerTitle: 'Diensten en prijzen, in gewone taal',
    eyebrow: 'Wat je krijgt',
    intro:
      'Een website die er goed uitziet, overal werkt en nooit achterop raakt. Voor één eenvoudig maandbedrag, zonder iets vooraf te betalen.',
    includedEyebrow: 'Elke site bevat',
    includedTitle: 'Alles wat je zaak online nodig heeft',
    model: {
      eyebrow: 'Het maandmodel',
      title: '€150 per maand. Geen opstartkosten. Updates inbegrepen.',
      body: 'In plaats van een hoge rekening voordat er iets bestaat, betaal je een eenvoudig maandbedrag. Dat dekt het ontwerp, de bouw, de hosting en het doorlopend onderhoud, allemaal samen. Je site blijft verzorgd zolang we samenwerken.',
      list: [
        'Niets vooraf te betalen',
        'Ontwerp, bouw en hosting inbegrepen',
        'Regelmatige updates en aanpassingen, gratis',
        'Zonder addertjes onder het gras',
      ],
    },
    compare: {
      mineTag: 'DS-Innovation',
      mineStrong: '€0 vooraf',
      mineNote: 'daarna €150 per maand, updates inbegrepen',
      themTag: 'Gemiddeld bureau',
      themStrong: '€300+ vooraf',
      themNote: 'daarna extra voor elke aanpassing',
      badge: 'Aanrader',
      foot: 'Hetzelfde vakwerk, op een vriendelijkere manier betaald.',
    },
    tiersEyebrow: 'Eenvoudige pakketten',
    tiersTitle: 'Kies wat bij je past',
    tiersLead: 'Weet je niet welk pakket? Neem gewoon contact op, we zoeken het samen uit.',
    tierCta: 'Aan de slag',
    ribbon: 'Populairst',
    stepsEyebrow: 'Hoe we werken',
    stepsTitle: 'Van eerste gesprek tot een site die met je meegroeit',
    cta: {
      title: 'Laten we het over je website hebben',
      body: 'Geen druk en geen vakjargon. Vertel me over je zaak en ik neem het van daar over.',
      button: 'Vertel me over je zaak',
    },
  },

  work: {
    headerTitle: 'Werk waar ik trots op ben',
    eyebrow: 'Portfolio',
    intro:
      'Echte websites voor echte ondernemingen. Samen ontworpen, gebouwd om lang mee te gaan, en na de lancering up-to-date gehouden.',
    featuredPrefix: 'Uitgelicht · ',
    story: {
      needHead: 'Wat ze nodig hadden',
      needBody: 'Een site die net zo verzorgd is als hun talent: snel, modern en makkelijk actueel te houden.',
      howHead: 'Hoe we het aanpakten',
      howBody:
        'We bepaalden het samen, ik deelde schetsen waarop ze reageerden, daarna bouwde en verfijnde ik het van daaruit.',
      sinceHead: 'Sinds de lancering',
      sinceBody: 'Nog steeds regelmatig bijgewerkt terwijl het bureau groeit: nieuwe gezichten, nieuw werk, zonder gedoe.',
    },
    flagshipCta: 'Bezoek de live site',
    moreEyebrow: 'Meer werk',
    moreTitle: 'Meer projecten op komst',
    moreLead: 'Nieuwe cases worden hier toegevoegd zodra ze live gaan.',
    comingSoon: 'Binnenkort',
    screenshotSoon: 'Schermafbeelding volgt',
    visitLive: 'Bezoek de live site',
    cta: {
      title: 'Kan jouw zaak de volgende zijn?',
      body: 'Vertel me wat je voor ogen hebt en ik laat je zien wat er mogelijk is. Geen druk, geen vakjargon.',
      button: 'Vertel me over je zaak',
    },
  },

  about: {
    headerTitle: 'Hallo, ik ben Daniel',
    eyebrow: 'Over mij',
    intro:
      'Ik ontwerp en bouw websites voor kleine ondernemingen, en ik houd ze na de lancering netjes bij zodat ze voor je blijven werken.',
    photoInitials: 'DS',
    photoAlt: 'Daniel, oprichter van DS-Innovation',
    paras: [
      "Ik ben Daniel, de persoon achter DS-Innovation. Ik bouw websites voor kleine ondernemingen: de restaurants, kappers, bloemisten en vakmensen die geweldig zijn in wat ze doen, maar niet de tijd (of het geduld) hebben om met het web te worstelen.",
      'Mijn taak is om dat deel makkelijk te maken. We bespreken wat je echt nodig hebt, ik laat je ideeën zien waarop je kunt reageren, en ik bouw iets strak en snel dat de klanten binnenhaalt waar het jou om gaat. Daarna houd ik het bij terwijl je zaak groeit.',
      'Geen bureaus, geen accountmanagers, geen vakjargon. Gewoon één persoon die de tijd neemt om je zaak te begrijpen en het voor elkaar krijgt.',
    ],
    why: {
      eyebrow: 'Waarom maandelijks?',
      title: 'Een eerlijker deal voor kleine ondernemingen',
      body1:
        'De meeste studio’s vragen een grote som vooraf en verdwijnen daarna. Ik denk niet dat dat werkt voor een kleine zaak. Dus spreid ik de kosten in een eenvoudig maandbedrag, vanaf €150 per maand en zonder iets vooraf, en dat bedrag omvat het onderhoud en de updates van je site.',
      body2:
        'Zo krijg je een professionele site zonder schrikwekkende rekening, en wordt het nooit dat ding dat je één keer bouwde en daarna nooit meer aanraakte. Het groeit met je mee.',
      link: 'Bekijk hoe de prijzen werken',
    },
    stats: [
      { value: '€0', label: 'om te starten' },
      { value: '€150', label: 'per maand, alles inbegrepen' },
      { value: '1:1', label: 'je werkt rechtstreeks met mij' },
      { value: '∞', label: 'updates zolang we samenwerken' },
    ],
    processEyebrow: 'Mijn aanpak',
    processTitle: 'Eenvoudig, en helemaal rond jou gebouwd',
    stepLabel: 'Stap',
    cta: {
      title: 'Laten we samen iets bouwen waar je trots op bent',
      body: 'Vertel me iets over je zaak en ik kom bij je terug met een paar ideeën.',
      button: 'Vertel me over je zaak',
    },
  },

  contact: {
    headerTitle: 'Vertel me over je zaak',
    eyebrow: 'Contact',
    intro:
      'Vertel me over je zaak en ik kom bij je terug, meestal binnen een dag. Geen druk, geen vakjargon, geen verplichting.',
    detailsTitle: 'Laten we praten',
    detailsBody: 'Liever rechtstreeks contact? Zo bereik je me het snelst.',
    labels: { email: 'E-mail', phone: 'Telefoon', linkedin: 'LinkedIn', location: 'Locatie' },
    locationValue: 'Antwerpen / Kapellen, België',
    reassure: 'Je gegevens blijven bij mij. Ik gebruik ze alleen om op je bericht te antwoorden.',
    form: {
      firstName: 'Voornaam',
      lastName: 'Achternaam',
      email: 'E-mail',
      phone: 'Telefoon',
      business: 'Je zaak',
      whatInMind: 'Wat heb je in gedachten?',
      firstNamePh: 'Jan',
      lastNamePh: 'Jansen',
      emailPh: 'jan@jouwzaak.be',
      phonePh: '+32 483 12 34 56',
      businessPh: 'Bakkerij Jansen',
      messagePh: 'Vertel me iets over je zaak en wat je website zou moeten doen.',
      send: 'Verstuur bericht',
      fallbackPre: 'Liever e-mailen? Schrijf naar ',
      enquiryPrefix: 'Mijn vraag gaat over: ',
      successTitle: 'Bedankt, bericht ontvangen!',
      successBody: 'Ik kom zo snel mogelijk bij je terug, meestal binnen een dag.',
      error: 'Er ging iets mis bij het versturen van je bericht. Je kunt me ook rechtstreeks mailen.',
      errorLink: 'Mail me rechtstreeks',
      validation: 'Kun je de gemarkeerde velden even nakijken?',
    },
  },

  process: [
    {
      icon: 'chat',
      title: 'We bespreken het',
      description:
        'We gaan samen zitten en bepalen precies wat je zaak nodig heeft: wie je wilt bereiken en wat de site moet doen. Geen vakjargon, gewoon een goed gesprek.',
    },
    {
      icon: 'sketch',
      title: 'Ik schets het idee',
      description:
        'Ik deel concepten en eerste ontwerpen zodat je ziet welke kant het op gaat. Jij reageert, ik verfijn, tot het goed voelt voor je zaak.',
    },
    {
      icon: 'build',
      title: 'Ik bouw het',
      description:
        'Ik verander het ontwerp in een snelle, mobielvriendelijke website die er op elke telefoon, tablet en laptop prachtig uitziet en makkelijk te gebruiken is voor je klanten.',
    },
    {
      icon: 'refresh',
      title: 'Ik houd het fris',
      description:
        'Je site veroudert niet. Ik onderhoud en werk hem in de loop van de tijd bij: nieuwe foto’s, aanbiedingen, openingsuren, allemaal inbegrepen in je maandbedrag.',
    },
  ],

  features: [
    {
      icon: 'phone',
      title: 'Ziet er op elk scherm goed uit',
      description:
        'Mobiel eerst ontworpen, dus het ziet er scherp uit of een klant nu op zijn telefoon, tablet of computer zit.',
    },
    {
      icon: 'gauge',
      title: 'Snel geladen',
      description: 'Vlotte pagina’s die bezoekers niet laten wachten, want een trage site jaagt klanten weg.',
    },
    {
      icon: 'search',
      title: 'Vindbaar op Google',
      description:
        'Zo gebouwd dat zoekmachines hem goed kunnen lezen, zodat je zaak meer kans maakt om gevonden te worden.',
    },
    {
      icon: 'pencil',
      title: 'Makkelijk bij te werken',
      description: 'Moet je je menu, prijzen of openingsuren aanpassen? Zeg het gewoon, bijwerken hoort erbij.',
    },
    {
      icon: 'lock',
      title: 'Veilig en betrouwbaar',
      description: 'Een veilige verbinding en betrouwbare hosting, zodat je site online blijft en je bezoekers veilig zijn.',
    },
    {
      icon: 'support',
      title: 'Een echt persoon om te bellen',
      description:
        'Je hebt rechtstreeks contact met mij. Geen callcenters, geen ticketnummers. Gewoon duidelijke antwoorden.',
    },
  ],

  tiers: [
    {
      name: 'Starter',
      scope: 'Een verzorgde website van één pagina',
      price: '€150',
      priceNote: 'per maand · geen opstartkosten',
      highlighted: false,
      features: [
        'Website van één pagina',
        'Mobiel eerst, snel en veilig',
        'Contactformulier en kaart',
        'Updates inbegrepen',
        'Basis vindbaarheid op Google',
      ],
    },
    {
      name: 'Business',
      scope: 'Een website met meerdere pagina’s voor een groeiende zaak',
      price: '€150+',
      priceNote: 'per maand · geen opstartkosten',
      highlighted: true,
      features: [
        'Alles uit Starter',
        'Meerdere pagina’s (diensten, menu, galerij en meer)',
        'Boekings- of aanvraagformulier',
        'Fotogalerijen en content',
        'Updates met voorrang',
      ],
    },
    {
      name: 'Op maat',
      scope: 'Iets meer op maat',
      price: 'Even overleggen',
      priceNote: 'nog steeds maandelijks · nog steeds geen hoge rekening vooraf',
      highlighted: false,
      features: [
        'Alles uit Business',
        'Functies op maat van je vak',
        'Online bestellen en koppelingen',
        'Meertalig',
        'Hands-on, doorlopende ondersteuning',
      ],
    },
  ],

  trust: [
    {
      icon: 'euro',
      title: 'Geen opstartkosten',
      description:
        "De meeste studio’s vragen €300 of meer voordat ze beginnen. Bij mij start je vanaf €150 per maand, zonder iets vooraf.",
    },
    {
      icon: 'clock',
      title: 'Nooit verouderd',
      description:
        'Je site wordt na de lancering onderhouden en bijgewerkt. Hij groeit mee met je zaak, niet ertegenin.',
    },
    {
      icon: 'handshake',
      title: 'Rechtstreeks contact met mij',
      description: 'Eén persoon die je project door en door kent. Geen bureaus, geen tussenpersonen, geen vakjargon.',
    },
    {
      icon: 'heart',
      title: 'Gemaakt voor kleine ondernemingen',
      description:
        'Restaurants, kappers, bloemisten, vakmensen. Ik bouw sites die de klanten binnenhalen waar het jou om gaat.',
    },
  ],

  caseStudies: [
    {
      name: 'Astral Models',
      url: 'https://www.astralmodels.com/women',
      tag: 'Modellenbureau',
      description:
        'Een hedendaags modellenbureau dat een site nodig had die net zo verzorgd is als hun talent. We bepaalden samen precies wat ze nodig hadden, ik deelde schetsen waarop ze reageerden, daarna bouwde ik een strakke, snelle site. Nog steeds regelmatig bijgewerkt terwijl ze groeien.',
      image: astralImg as string | null,
      isPlaceholder: false,
    },
  ],
};

export type Dict = typeof nl;
export type Lang = 'nl' | 'en';

const en: Dict = {
  skipToContent: 'Skip to content',

  nav: {
    home: 'Home',
    services: 'Services',
    work: 'Work',
    about: 'About',
    contact: 'Contact',
    cta: 'Get in touch',
    language: 'Language',
    toDark: 'Dark mode',
    toLight: 'Light mode',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
  },

  footer: {
    tagline:
      'Websites for small businesses. Beautifully built, kept fresh, from €150/month with no upfront cost.',
    exploreTitle: 'Explore',
    contactTitle: 'Get in touch',
    location: 'Antwerp / Kapellen, Belgium',
    links: { home: 'Home', services: 'Services & pricing', work: 'Work', about: 'About', contact: 'Contact' },
  },

  home: {
    eyebrow: 'Websites for small businesses',
    titlePart1: 'A website your business will be ',
    titleAccent: 'proud of.',
    titlePart2: '',
    leadPre: 'Beautiful, fast websites for restaurants, salons, florists and tradespeople. From ',
    leadStrong: '€150/month with no upfront cost',
    leadPost: ', kept fresh and updated long after launch.',
    ctaPrimary: 'Tell me about your business',
    ctaSecondary: 'See my work',
    chips: ['No upfront cost', 'Updates included', 'You deal with me directly'],
    value: {
      from: 'From',
      price: '€150',
      unit: '/mo',
      priceNote: 'No upfront cost. Ever.',
      typicalStudio: 'Typical studio',
      oldPrice: '€300+',
      oldNote: 'upfront, before a single page exists',
      title: 'Skip the big upfront bill.',
      body: 'Most studios charge €300 or more before they start, then leave you to it. I don’t. You spread the cost into a simple monthly price, and I keep your site maintained and up to date for as long as we work together.',
      link: 'How the monthly model works',
    },
    processEyebrow: 'How it works',
    processTitle: 'Four simple steps, no jargon',
    processLead: 'From first chat to a site that keeps getting better. Here is how we get there.',
    teaser: {
      eyebrow: 'Featured work',
      title: 'Astral Models',
      body: 'A contemporary model agency that needed a site as polished as their talent. We worked out exactly what they needed, I shared sketches they reacted to, and I built a clean, fast site. It is still updated regularly as they grow.',
      cta: 'See the case study',
      live: 'Visit the live site',
    },
    trustEyebrow: 'Why me',
    trustTitle: 'Straightforward, and on your side',
    cta: {
      title: 'Ready for a website that works as hard as you do?',
      body: 'Tell me a little about your business and I will get back to you with ideas. No pressure, no jargon.',
      primary: 'Tell me about your business',
      link: 'See services and pricing',
    },
  },

  services: {
    headerTitle: 'Services and pricing, in plain English',
    eyebrow: 'What you get',
    intro:
      'A website that looks the part, works everywhere, and never gets left behind. For one simple monthly price with nothing to pay up front.',
    includedEyebrow: 'Every site includes',
    includedTitle: 'Everything your business needs online',
    model: {
      eyebrow: 'The monthly model',
      title: '€150/month. No upfront cost. Updates included.',
      body: 'Instead of a big bill before anything exists, you pay a simple monthly price. That covers the design, the build, the hosting and the ongoing updates, all in one. Your site stays looked after for as long as we work together.',
      list: [
        'Nothing to pay up front',
        'Design, build and hosting included',
        'Regular updates and tweaks, on the house',
        'No lock-in tricks',
      ],
    },
    compare: {
      mineTag: 'DS-Innovation',
      mineStrong: '€0 upfront',
      mineNote: 'then €150/month, updates included',
      themTag: 'Typical studio',
      themStrong: '€300+ upfront',
      themNote: 'then extra for every change',
      badge: 'Recommended',
      foot: 'Same quality of work, a friendlier way to pay for it.',
    },
    tiersEyebrow: 'Simple tiers',
    tiersTitle: 'Pick the scope that fits',
    tiersLead: 'Not sure which one? Just get in touch, we will figure it out together.',
    tierCta: 'Get started',
    ribbon: 'Most popular',
    stepsEyebrow: 'How we work',
    stepsTitle: 'From first chat to a site that grows with you',
    cta: {
      title: 'Let’s talk about your website',
      body: 'No pressure and no jargon. Just tell me about your business and I will take it from there.',
      button: 'Tell me about your business',
    },
  },

  work: {
    headerTitle: 'Work I’m proud of',
    eyebrow: 'Portfolio',
    intro:
      'Real websites for real businesses. Designed together, built to last, and kept up to date long after launch.',
    featuredPrefix: 'Featured · ',
    story: {
      needHead: 'What they needed',
      needBody: 'A site as polished as their talent: fast, modern, and easy to keep current.',
      howHead: 'How we got there',
      howBody: 'We scoped it together, I shared sketches they reacted to, then I built and refined it from there.',
      sinceHead: 'Since launch',
      sinceBody: 'Still updated regularly as the agency grows: new faces, new work, no fuss.',
    },
    flagshipCta: 'Visit the live site',
    moreEyebrow: 'More work',
    moreTitle: 'More projects on the way',
    moreLead: 'Fresh case studies are being added here as they go live.',
    comingSoon: 'Coming soon',
    screenshotSoon: 'Screenshot coming soon',
    visitLive: 'Visit the live site',
    cta: {
      title: 'Could your business be next?',
      body: 'Tell me what you have in mind and I will show you what is possible. No pressure, no jargon.',
      button: 'Tell me about your business',
    },
  },

  about: {
    headerTitle: 'Hi, I’m Daniel',
    eyebrow: 'About',
    intro:
      'I design and build websites for small businesses, and I keep them looking after your business long after they go live.',
    photoInitials: 'DS',
    photoAlt: 'Daniel, founder of DS-Innovation',
    paras: [
      'I’m Daniel, the person behind DS-Innovation. I build websites for small businesses: the restaurants, salons, florists and tradespeople who are brilliant at what they do, but don’t have the time (or the patience) to wrestle with the web.',
      'My job is to make that part easy. We talk through what you actually need, I show you ideas you can react to, and I build something clean and fast that brings in the customers you care about. Then I keep it updated as your business grows.',
      'No agencies, no account managers, no jargon. Just one person who takes the time to understand your business and gets it done.',
    ],
    why: {
      eyebrow: 'Why monthly?',
      title: 'A fairer deal for small businesses',
      body1:
        'Most studios ask for a big cheque up front and then disappear. I don’t think that works for a small business. So I spread the cost into a simple monthly price, from €150/month with nothing to pay up front, and that price includes keeping your site maintained and up to date.',
      body2:
        'It means you get a professional site without a scary bill, and it never becomes that thing you built once and never touched again. It grows with you.',
      link: 'See how pricing works',
    },
    stats: [
      { value: '€0', label: 'to get started' },
      { value: '€150', label: 'per month, all in' },
      { value: '1:1', label: 'you work with me directly' },
      { value: '∞', label: 'updates while we work together' },
    ],
    processEyebrow: 'My process',
    processTitle: 'Simple, and built around you',
    stepLabel: 'Step',
    cta: {
      title: 'Let’s build something you’re proud of',
      body: 'Tell me a little about your business and I will get back to you with a few ideas.',
      button: 'Tell me about your business',
    },
  },

  contact: {
    headerTitle: 'Tell me about your business',
    eyebrow: 'Contact',
    intro:
      'Tell me about your business and I will get back to you, usually within a day. No pressure, no jargon, no obligation.',
    detailsTitle: 'Let’s talk',
    detailsBody: 'Prefer to reach out directly? Here is the quickest way to get me.',
    labels: { email: 'Email', phone: 'Phone', linkedin: 'LinkedIn', location: 'Location' },
    locationValue: 'Antwerp / Kapellen, Belgium',
    reassure: 'Your details stay with me. I will only use them to reply to your enquiry.',
    form: {
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      phone: 'Phone',
      business: 'Your business',
      whatInMind: 'What do you have in mind?',
      firstNamePh: 'Jane',
      lastNamePh: 'Baker',
      emailPh: 'jane@yourbusiness.be',
      phonePh: '+32 483 12 34 56',
      businessPh: 'Jane’s Bakery',
      messagePh: 'Tell me a little about your business and what you’d like your website to do.',
      send: 'Send message',
      fallbackPre: 'Prefer email? Write to ',
      enquiryPrefix: 'My enquiry is about: ',
      successTitle: 'Thanks, message received!',
      successBody: 'I will get back to you as soon as I can, usually within a day.',
      error: 'Something went wrong sending your message. You can email me directly instead.',
      errorLink: 'Email me directly',
      validation: 'Could you double-check the highlighted fields for me?',
    },
  },

  process: [
    {
      icon: 'chat',
      title: 'We talk it through',
      description:
        'We sit down and figure out exactly what your business needs: who you want to reach and what you want the site to do. No jargon, just a proper conversation.',
    },
    {
      icon: 'sketch',
      title: 'I sketch the idea',
      description:
        'I share concepts and rough designs so you can see where it is going. You react, I refine, until it feels right for your business.',
    },
    {
      icon: 'build',
      title: 'I build it',
      description:
        'I turn the design into a fast, mobile-friendly website that works beautifully on every phone, tablet and laptop, and is easy for your customers to use.',
    },
    {
      icon: 'refresh',
      title: 'I keep it fresh',
      description:
        'Your site does not go stale. I maintain and update it over time: new photos, offers, opening hours, all included in your monthly price.',
    },
  ],

  features: [
    {
      icon: 'phone',
      title: 'Looks great on every screen',
      description: 'Designed mobile-first, so it looks sharp whether a customer is on their phone, tablet or computer.',
    },
    {
      icon: 'gauge',
      title: 'Fast to load',
      description: 'Speedy pages that do not keep visitors waiting, because a slow site sends customers elsewhere.',
    },
    {
      icon: 'search',
      title: 'Found on Google',
      description: 'Built so search engines can read it properly, giving your business a better chance of being found.',
    },
    {
      icon: 'pencil',
      title: 'Easy to update',
      description: 'Need to change your menu, prices or opening hours? Just tell me, updating it is part of the deal.',
    },
    {
      icon: 'lock',
      title: 'Secure and reliable',
      description: 'A secure connection and dependable hosting, so your site stays online and your visitors stay safe.',
    },
    {
      icon: 'support',
      title: 'A real person to call',
      description: 'You deal with me directly. No call centres, no ticket numbers. Just straight answers.',
    },
  ],

  tiers: [
    {
      name: 'Starter',
      scope: 'A polished one-page site',
      price: '€150',
      priceNote: 'per month · no upfront cost',
      highlighted: false,
      features: [
        'Single-page website',
        'Mobile-first, fast and secure',
        'Contact form and map',
        'Ongoing updates included',
        'Found on Google basics',
      ],
    },
    {
      name: 'Business',
      scope: 'A multi-page site for a growing business',
      price: '€150+',
      priceNote: 'per month · no upfront cost',
      highlighted: true,
      features: [
        'Everything in Starter',
        'Multiple pages (services, menu, gallery and more)',
        'Booking or enquiry flow',
        'Photo galleries and content',
        'Priority updates',
      ],
    },
    {
      name: 'Tailored',
      scope: 'Something more bespoke',
      price: 'Let’s talk',
      priceNote: 'still monthly · still no big upfront bill',
      highlighted: false,
      features: [
        'Everything in Business',
        'Custom features for your trade',
        'Online ordering and integrations',
        'Multi-language',
        'Hands-on, ongoing support',
      ],
    },
  ],

  trust: [
    {
      icon: 'euro',
      title: 'No upfront cost',
      description: 'Most studios ask for €300 or more before they start. With me you start from €150/month and nothing up front.',
    },
    {
      icon: 'clock',
      title: 'Never left to go stale',
      description: 'Your site is maintained and updated after launch. It grows with your business, not against it.',
    },
    {
      icon: 'handshake',
      title: 'You work with me directly',
      description: 'One person who knows your project inside out. No agencies, no hand-offs, no jargon.',
    },
    {
      icon: 'heart',
      title: 'Built for small businesses',
      description:
        'Restaurants, salons, florists, tradespeople. I build sites that win the customers you care about.',
    },
  ],

  caseStudies: [
    {
      name: 'Astral Models',
      url: 'https://www.astralmodels.com/women',
      tag: 'Model agency',
      description:
        'A contemporary model agency that needed a site as polished as their talent. We worked out exactly what they needed, I shared sketches they reacted to, then I built a clean, fast site. It is still updated regularly as they grow.',
      image: astralImg,
      isPlaceholder: false,
    },
  ],
};

export const translations: Record<Lang, Dict> = { nl, en };
